import React, { useState, useEffect } from 'react';
import { getUserSoilClimate, getEstimationMeetData } from '../firebase/firestoreService';
import namemapping from '../utils/namemapping'; // Import the swapped mapping
import { Pie } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';

// Reusable StatCard Component
const StatCard = ({ title, value }) => (
  <div style={styles.statCard}>
    <div style={styles.statTitle}>{title}</div>
    <div style={styles.statValue}>{value}</div>
  </div>
);

// Reusable Table Row for each crop
const CropTableRow = ({ cropData, index, email }) => {
  const { cropName, probability } = cropData;
  const { demand, met } = cropData.estimationData;

  const demandMetPercentage = ((met / demand) * 100).toFixed(2);
  const needToGet = (demand - met).toFixed(2);

  const cropPieChartData = {
    labels: ['Met', 'Not Met'],
    datasets: [
      {
        data: [demandMetPercentage, 100 - demandMetPercentage],
        backgroundColor: ['#27ae60', '#f39c12'], // Green for met, Orange for unmet
        hoverOffset: 4,
      },
    ],
  };

  const displayName = namemapping[cropName] || cropName.toUpperCase();

  const navigate = useNavigate();  // Initialize useNavigate hook

  // Function to handle button click and navigate to add route
  const handleSowingClick = () => {
    navigate(`/add`, { state: { email, cropName } });  // Navigate to "/add" with email and cropName as state
  };

  return (
    <tr
      key={cropName}
      style={index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd}
    >
      <td style={styles.td}>{displayName}</td>
      <td style={styles.td}>{probability.toFixed(3)}</td>
      <td style={styles.td}>{demand.toFixed(3)}</td>
      <td style={styles.td}>{met.toFixed(3)}</td>
      <td style={styles.td}>{needToGet}</td>
      <td style={styles.td}>
        <div style={styles.pieChartWrapper}>
          <Pie
            data={cropPieChartData}
            options={{
              responsive: true,
              plugins: { title: { display: false, text: false }, legend: { display: false } },
            }}
          />
        </div>
      </td>
      {/* New Action Column with button */}
      <td style={styles.td}>
        <button onClick={handleSowingClick} style={styles.actionButton}>
          Sowing the Crop
        </button>
      </td>
    </tr>
  );
};

const RecommandBasedOnSoilClimate = ({ email }) => {
  const [userCrops, setUserCrops] = useState([]);
  const [estimationData, setEstimationData] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const soilClimateData = await getUserSoilClimate(email);
        const estimationDataFromFirebase = await getEstimationMeetData();

        const estimationDataMap = Object.keys(estimationDataFromFirebase).reduce(
          (map, crop) => {
            const standardizedCropName = crop.toLowerCase();
            map[standardizedCropName] = estimationDataFromFirebase[crop];
            return map;
          },
          {}
        );

        const sortedUserCrops = soilClimateData
          .filter((crop) => crop[Object.keys(crop)[0]].predicted_probability > 0.1)
          .sort((a, b) =>
            b[Object.keys(b)[0]].predicted_probability - a[Object.keys(a)[0]].predicted_probability
          );

        setUserCrops(sortedUserCrops);
        setEstimationData(estimationDataMap);
      } catch (err) {
        console.error('Error fetching data: ', err);
      }
    };

    fetchData();
  }, [email]);

  // Get the crop name and probability from user crops
  const getCropNameAndProbability = (cropData) => {
    const cropName = Object.keys(cropData)[0]; // Crop name in proper case
    const probability = cropData[cropName].predicted_probability;
    return { cropName, probability };
  };

  const getEstimationDataForCrop = (cropName) => {
    const standardizedCropName = cropName.toLowerCase();
    const cropData = estimationData[standardizedCropName];
    return cropData ? cropData : { met: 0, demand: 0 };
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.header}>Recommended Crops Based on Soil and Climate</h1>
      <div style={styles.statsContainer}>
        <StatCard title="Total Crops" value={userCrops.length} />
      </div>
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Crop Name</th>
              <th style={styles.th}>Probability</th>
              <th style={styles.th}>Demand</th>
              <th style={styles.th}>Met</th>
              <th style={styles.th}>Need</th>
              <th style={styles.th}>Demand Met Breakdown</th>
              <th style={styles.th}>Action</th> {/* New column header */}
            </tr>
          </thead>
          <tbody>
            {userCrops.map((cropData, index) => {
              const { cropName, probability } = getCropNameAndProbability(cropData);
              const { demand, met } = getEstimationDataForCrop(cropName);
              const estimationData = { demand, met }; // Passed to Pie chart
              return (
                <CropTableRow
                  cropData={{ cropName, probability, estimationData }}
                  index={index}
                  key={cropName}
                  email={email}  
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  container: {
    margin: '40px auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    width: '90%',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    border: '2px solid rgba(255, 255, 255, 0.9)',
  },
  header: {
    fontSize: '24px',
    fontWeight: '600',
    padding: '0px 0px 10px',
    textAlign: 'center',
    color: '#ffff',
  },
  statsContainer: {
    display: 'flex',
    justifyContent: 'space-evenly',
    marginBottom: '20px',
    gap: '20px',
  },
  statCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    padding: '15px',
    borderRadius: '8px',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.4)',
    textAlign: 'center',
    width: '180px',
  },
  statTitle: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#555',
  },
  statValue: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#2d3d4c',
  },
  actionButton: {
    padding: '8px 16px',
    fontSize: '14px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'center',
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: '12px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    borderRadius: '12px',
    tableLayout: 'fixed',
  },
  th: {
    padding: '8px 16px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    textAlign: 'left',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  td: {
    padding: '8px 16px',
    textAlign: 'left',
    borderBottom: '1px solid #ddd',
    fontSize: '14px',
  },
  tableRowEven: {
    backgroundColor: '#f9f9f9',
  },
  tableRowOdd: {
    backgroundColor: '#ffffff',
  },
  pieChartWrapper: {
    width: '80px',
    height: '80px',
    margin: '0 auto',
  },
};

export default RecommandBasedOnSoilClimate;
